import { Box, Stack, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { CellWithSecondaryText } from "components/DataGrid";
import { ExportDataButton } from "components/ExportDataButton";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { renderCopyableCell } from "components/Typography/copyableCell";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { formatAddress } from "encamp-shared/src/utils/address";
import {
  federalWasteGeneratorStatusToLabel,
  stateGeneratorStatusRank,
  stateWasteGeneratorStatusToLabel,
} from "encamp-shared/src/waste/index";
import { gql } from "generated-graphql";
import {
  RcraInfoFederalWasteGeneratorStatus,
  StateRegulatoryStatus,
  WasteFacilitiesQuery,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useManifestViewsV2 } from "hooks/useManifestViewsV2";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { stateOptionLabel } from "util/filterOptionLabels";

import {
  SITE_MANAGER_ACCESS_FILTER_VALUES,
  SITE_MANAGER_ACCESS_TOOLTIP,
  siteManagerAccessToLabel,
} from "../siteManagerAccess";
import { CalculatedStatusCell } from "./CalculatedStatusCell";
import { calculatedStatusColumnTooltip } from "./calculatedStatusCopy";

/**
 * Column header with an info icon explaining how the column is calculated. A row's
 * own icon means something else: that facility's sources disagree.
 */
export function CalculatedStatusHeader({
  title,
  paragraphs,
}: {
  title: string;
  paragraphs: string[];
}) {
  return (
    <Stack direction="row" alignItems="center" sx={{ minWidth: 0, width: "100%" }}>
      {/* The title shrinks, the icon never does, so a narrow column keeps the
          only explanation of how it is calculated. */}
      <Typography
        fontWeight="medium"
        fontSize="14px"
        sx={{ whiteSpace: "normal", lineHeight: 1.25, minWidth: 0, flex: "0 1 auto" }}
      >
        {title}
      </Typography>
      {/* Opening the tooltip must not re-sort the grid, which sorts on both header
          click and Enter, whatever was focused. */}
      <Box
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        sx={{ display: "flex" }}
      >
        <InfoIconTooltip
          tooltip={
            <Stack gap={1}>
              {paragraphs.map((paragraph) => (
                <Typography key={paragraph} variant="caption">
                  {paragraph}
                </Typography>
              ))}
            </Stack>
          }
          ariaLabel={`About ${title}`}
          fontSize="1rem"
          iconButtonSx={{ p: 0.25, flexShrink: 0 }}
        />
      </Box>
    </Stack>
  );
}

const PREFERRED_GENERATOR_STATUS_ORDER = [
  RcraInfoFederalWasteGeneratorStatus.VerySmallQuantityGenerator,
  RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator,
  RcraInfoFederalWasteGeneratorStatus.LargeQuantityGenerator,
  RcraInfoFederalWasteGeneratorStatus.Pending,
  RcraInfoFederalWasteGeneratorStatus.NotAGenerator,
  RcraInfoFederalWasteGeneratorStatus.Undetermined,
];

const SORTED_WASTE_GENERATOR_STATUSES = Object.values(RcraInfoFederalWasteGeneratorStatus)
  .sort((a, b) => {
    const aIndex = PREFERRED_GENERATOR_STATUS_ORDER.indexOf(a);
    const bIndex = PREFERRED_GENERATOR_STATUS_ORDER.indexOf(b);
    return aIndex - bIndex;
  })
  .concat("None" as unknown as RcraInfoFederalWasteGeneratorStatus);

/**
 * State filter options by tier, smallest first, like the federal dropdown. Non-tier
 * values such as `SAME_AS_FEDERAL` are left out; no user would filter for them.
 */
const SORTED_STATE_GENERATOR_STATUSES = Object.values(StateRegulatoryStatus)
  .filter((status) => stateGeneratorStatusRank(status) > 0)
  .sort((a, b) => stateGeneratorStatusRank(a) - stateGeneratorStatusRank(b))
  .concat("None" as unknown as StateRegulatoryStatus);

type WasteFacilityRow = WasteFacilitiesQuery["wasteFacilities"]["items"][number];

/**
 * The year-to-date calculated status columns, one per axis. The state column
 * doesn't sort: a Postgres enum sort orders by stored name, not tier.
 */
function calculatedStatusColumn(axis: "federal" | "state"): OmnisearchGridColDef<WasteFacilityRow> {
  const federal = axis === "federal";
  const title = federal ? "Calculated Generator Status (YTD)" : "Calculated State Status (YTD)";
  return {
    field: federal ? "shipmentWasteGeneratorStatus" : "shipmentStateWasteGeneratorStatus",
    headerName: title,
    // Wide enough for the title to keep its line count when the sort arrow
    // appears on hover and takes width out of the header.
    minWidth: federal ? 215 : 185,
    flex: 0.4,
    sortable: federal,
    headerClassName: "wrapHeader",
    filterKeyType: "enumAutocomplete",
    enumValues: federal ? SORTED_WASTE_GENERATOR_STATUSES : SORTED_STATE_GENERATOR_STATUSES,
    // State values like "MINI_QUANTITY_GENERATOR" read as the column renders them;
    // federal keeps the default prettifier.
    enumPresentationFunction: federal ? undefined : stateWasteGeneratorStatusToLabel,
    valueGetter(_, row) {
      return federal
        ? federalWasteGeneratorStatusToLabel(
            row.shipmentWasteGeneratorStatus ?? RcraInfoFederalWasteGeneratorStatus.Undetermined
          )
        : stateWasteGeneratorStatusToLabel(row.shipmentStateWasteGeneratorStatus ?? null);
    },
    renderHeader: () => (
      <CalculatedStatusHeader title={title} paragraphs={calculatedStatusColumnTooltip(axis)} />
    ),
    renderCell: ({ value, row }) => {
      const attribution = federal
        ? row.shipmentWasteGeneratorStatusAttribution
        : row.shipmentStateWasteGeneratorStatusAttribution;
      return (
        <CalculatedStatusCell
          label={String(value)}
          sources={attribution?.bySource}
          axis={axis}
          effectiveSource={attribution?.source}
          month={attribution?.month}
        />
      );
    },
  };
}

const WASTE_FACILITIES = gql(`
  query WasteFacilities(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    wasteFacilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        facility {
          id
          name
          customerFacilityId
          streetAddress1
          city
          state
          zip
          facilityAlternateIds {
            id
            type
            value
            hasNoId
          }
        }
        epaId
        reportedWasteGeneratorStatus
        shipmentWasteGeneratorStatus
        shipmentStateWasteGeneratorStatus
        shipmentWasteGeneratorStatusAttribution {
          month
          source
          bySource {
            source
            federalStatus
            stateStatus
          }
        }
        shipmentStateWasteGeneratorStatusAttribution {
          month
          source
          bySource {
            source
            federalStatus
            stateStatus
          }
        }
        siteManagerAccess
      }
      count
    }
  }
`);

export const WasteFacilities = () => {
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Site-manager access surfaces only with the v2 manifest views (staff
  // always v2) — v1 tenants don't see access anywhere else (their manifest
  // views predate it), so don't introduce it here either.
  const showManifestViewsV2 = useManifestViewsV2();

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Facilities",
    },
  ]);

  const columns: OmnisearchGridColDef<WasteFacilityRow>[] = [
    {
      field: "epaId",
      headerName: "EPA ID",
      minWidth: 150,
      sortable: true,
      renderCell: renderCopyableCell,
    },
    {
      field: "name",
      headerName: "Facility",
      filterKeyType: "wasteFacility",
      renderCell: (params) => (
        <CellWithSecondaryText
          primaryValue={
            params.row.facility.customerFacilityId
              ? `${params.row.facility.name} (${params.row.facility.customerFacilityId})`
              : params.row.facility.name
          }
          secondaryValue={formatAddress(
            params.row.facility.streetAddress1,
            params.row.facility.city,
            params.row.facility.state,
            params.row.facility.zip
          )}
        />
      ),
      flex: 1,
    },
    {
      field: "reportedWasteGeneratorStatus",
      headerName: "Current reported status",
      minWidth: 185,
      flex: 0.4,
      sortable: true,
      headerClassName: "wrapHeader",
      filterKeyType: "enumAutocomplete",
      enumValues: SORTED_WASTE_GENERATOR_STATUSES,
      valueGetter(_, row) {
        return federalWasteGeneratorStatusToLabel(
          row.reportedWasteGeneratorStatus ?? RcraInfoFederalWasteGeneratorStatus.Undetermined
        );
      },
      renderCell({ value }) {
        return (
          <Tooltip title="Current federal generator status reported in RCRAInfo (EPA).">
            {value}
          </Tooltip>
        );
      },
    },
    calculatedStatusColumn("federal"),
    calculatedStatusColumn("state"),
    ...(showManifestViewsV2
      ? [
          {
            field: "siteManagerAccess",
            headerName: "RCRAInfo Site Manager Access",
            minWidth: 165,
            flex: 0.4,
            sortable: true,
            headerClassName: "wrapHeader",
            filterKeyType: "enumAutocomplete",
            enumValues: SITE_MANAGER_ACCESS_FILTER_VALUES,
            enumPresentationFunction: siteManagerAccessToLabel,
            valueGetter(_, row) {
              return siteManagerAccessToLabel(row.siteManagerAccess);
            },
            renderCell({ value }) {
              return <Tooltip title={SITE_MANAGER_ACCESS_TOOLTIP}>{value}</Tooltip>;
            },
          } satisfies OmnisearchGridColDef<
            WasteFacilitiesQuery["wasteFacilities"]["items"][number]
          >,
        ]
      : []),
  ];

  const renderCommandButtons = ({ search }: { search: string }) => [
    <ExportDataButton
      key="export"
      tenantId={tenantId ?? ""}
      exportType="wasteFacilities"
      fileName="waste-facilities"
      filter={search ? { search } : undefined}
      iconOnly
    />,
  ];

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      {
        name: "Facility Info",
        fields: ["name", "epaId", "customerFacilityId"],
      },
      {
        name: "Location",
        fields: ["streetAddress1", "city", "state", "zip"],
      },
      {
        name: "Status",
        fields: [
          "reportedWasteGeneratorStatus",
          "shipmentWasteGeneratorStatus",
          ...(showManifestViewsV2 ? ["siteManagerAccess"] : []),
        ],
      },
    ],
    [showManifestViewsV2]
  );

  const defaultSearch = `tenantId:${tenantId}`;

  return (
    <Box sx={{ py: 3 }}>
      <OmnisearchDataGrid
        persistenceKey="waste-facilities-table"
        isURLDriven
        columns={columns}
        dataQuery={WASTE_FACILITIES}
        getRowId={(row) => `${row.facility.id}-${row.epaId ?? "no-epaId"}`}
        getItems={(data) => data.wasteFacilities.items}
        getCount={(data) => data.wasteFacilities.count}
        noDataMessage={"No waste facilities have been added to this organization"}
        isRowSelectable={() => false}
        defaultSearch={defaultSearch}
        initialSortModel={[{ field: "name", sort: "asc" }]}
        rowHeight={"auto"}
        commandButtons={renderCommandButtons}
        additionalFilterColumns={[
          { header: "Facility ID", key: "customerFacilityId" },
          { header: "Street Address", key: "streetAddress1" },
          { header: "City", key: "city" },
          {
            header: "State/Territory",
            key: "state",
            filterKeyType: "enumAutocomplete",
            enumValues: Object.keys(STATE_NAMES),
            enumPresentationFunction: stateOptionLabel,
          },
          { header: "Zip", key: "zip" },
        ]}
        onRowClick={(params) => navigate(`${params.row.facility.id}`)}
        getRowLink={(row) => `./${row.facility.id}`}
        filterGroups={filterGroups}
        // Calculated state status and RCRAInfo site manager access are off by
        // default (users can add them via the column menu); state status is
        // only meaningful for states that don't follow the federal generator
        // category, and site manager access rolls out filter-first without
        // changing the default view.
        initialColumnVisibilityModel={{
          shipmentStateWasteGeneratorStatus: false,
          siteManagerAccess: false,
        }}
        columnVisibilityModel={{
          ...(isMobile && {
            epaId: false,
            reportedWasteGeneratorStatus: false,
            shipmentWasteGeneratorStatus: false,
            shipmentStateWasteGeneratorStatus: false,
            siteManagerAccess: false,
          }),
        }}
      />
    </Box>
  );
};
